import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import CISboholAdd from './cisBoholAdd';
import useBoholGetYear from '../Hooks/CisHooks/useBoholGetYear';
import useGetYears from '../Hooks/CisHooks/useGetYears';
import useDeleteCIS from '../Hooks/CisHooks/useDeleteCIS';
import useSubtotalsByYearCIS from '../Hooks/CisHooks/useSubtotalsByYearCIS';
import adminBg from '../Assets/bg/adminbg.jpg';
import Loader from '../Components/Loader';
import CisModal from './CisModal';
import DeleteConfirmationModal from '../Components/DeleteConfirmationModal';
import CISboholEdit from './cisBoholEdit';
import CISboholImport from './cisBoholimport';
import ExcelJS from 'exceljs';
import niaLogo from '../Assets/Logo.png';
import philippinesSeal from '../Assets/Bagong_Pilipinas_logo.png';

const CISsiqiojor = ({ darkMode, onSelect, isSidebarExpanded = false }) => {
  const CIS_MACHINERIES_RETURN_KEY = 'cisMachineriesReturn';
  const [selectedFile, setSelectedFile] = useState(null);
  const [showEditPanel, setShowEditPanel] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [showRightPanel, setShowRightPanel] = useState(false);
  const [showImportPanel, setShowImportPanel] = useState(false);
  const fileInputRef = useRef(null);
  const [showAllData, setShowAllData] = useState(true);
  const [selectedYear, setSelectedYear] = useState(null);
  const [addSuccessMessage, setAddSuccessMessage] = useState(null);
  const section = 'siquijor';
  const { years: yearData, loading: yearLoading, error: yearError, refetch: refetchYears } = useGetYears(section);
  const { data, loading, error, refetch} = useBoholGetYear(selectedYear, section);
  const { data: subtotals, loading: subtotalsLoading, error: subtotalsError } = useSubtotalsByYearCIS(selectedYear, section);
  const [headerImo, setHeaderImo] = useState(localStorage.getItem('headerImo') || 'BOHOL-CEBU-SIQUIJOR IMO');
  const [headerProfileDate, setHeaderProfileDate] = useState(localStorage.getItem('headerProfileDate') || 'CIS IA PROFILE as of March 31, 2025');
  const [headerRegion, setHeaderRegion] = useState(localStorage.getItem('headerRegion') || 'Region: 7');
  const [isEditingHeader, setIsEditingHeader] = useState(false);
  const { deleteCIS, loading: deleteLoading, error: deleteError } = useDeleteCIS();

  const role = (localStorage.getItem('role') || '').toLowerCase();
  const showControls = role === 'admin' || role === section;
  const hideControlsForViewOnly = !showControls;

  const saveHeader = () => {
    localStorage.setItem('headerImo', headerImo);
    localStorage.setItem('headerProfileDate', headerProfileDate);
    localStorage.setItem('headerRegion', headerRegion);
    setIsEditingHeader(false);
  };

  const handleImportSuccess = async (newRecords) => {
    console.log('Saving imported records:', newRecords);
    setAddSuccessMessage('Data imported successfully!');
    setTimeout(() => setAddSuccessMessage(null), 3000);
    setShowImportPanel(false);
    await refetch();
  };

  const handleAddSuccess = async (msg) => {
    setAddSuccessMessage(msg || 'Data added successfully!');
    setTimeout(() => setAddSuccessMessage(null), 3000);
  };

  useEffect(() => {
    if (yearLoading) return;

    if (yearError) {
      const currentYear = new Date().getFullYear();
      setSelectedYear(currentYear);
      return;
    }

    if (Array.isArray(yearData) && yearData.length > 0) {
      const validYears = yearData
        .filter(year => typeof year === 'number' && !isNaN(year))
        .sort((a, b) => b - a);
      if (validYears.length > 0) {
        setSelectedYear(prev => prev ?? validYears[0]);
      } else {
        setSelectedYear(new Date().getFullYear());
      }
    } else {
      setSelectedYear(new Date().getFullYear());
    }
  }, [yearData, yearLoading, yearError]);

  useEffect(() => {
    if (yearError && !yearLoading) {
      const retry = setTimeout(() => refetchYears(), 3000);
      return () => clearTimeout(retry);
    }
  }, [yearError, yearLoading, refetchYears]);

  useEffect(() => {
    const pending = localStorage.getItem(CIS_MACHINERIES_RETURN_KEY);
    if (!pending) return;

    try {
      const parsed = JSON.parse(pending);
      if (parsed?.sectionKey && parsed.sectionKey !== 'CISsiquijor') return;

      if (parsed?.selectedItem) {
        setSelectedFile(parsed.selectedItem);
        setModalOpen(true);
      }

      if (parsed?.selectedYear) {
        setSelectedYear(parsed.selectedYear);
      }

      localStorage.removeItem(CIS_MACHINERIES_RETURN_KEY);
    } catch (err) {
      localStorage.removeItem(CIS_MACHINERIES_RETURN_KEY);
    }
  }, []);

  useEffect(() => {
    const scrollContent = document.getElementById('scrollContent');
    const bottomScroll = document.getElementById('bottomScroll');

    if (scrollContent && bottomScroll) {
      const onBottomScroll = () => {
        scrollContent.scrollLeft = bottomScroll.scrollLeft;
      };

      const onContentScroll = () => {
        bottomScroll.scrollLeft = scrollContent.scrollLeft;
      };

      bottomScroll.addEventListener('scroll', onBottomScroll);
      scrollContent.addEventListener('scroll', onContentScroll);

      return () => {
        bottomScroll.removeEventListener('scroll', onBottomScroll);
        scrollContent.removeEventListener('scroll', onContentScroll);
      };
    }
  }, []);

  const handleClose = () => {
    setShowRightPanel(false);
  };

  const handleRowClick = (item) => {
    console.log('Row clicked:', item);
    setSelectedFile(item);
    setModalOpen(true);
    console.log('selectedFile:', item, 'modalOpen:', true);
  };

  const handleEditData = (item) => {
    setSelectedFile(item);
    setShowEditPanel(true);
  };

  const closeEditPanel = () => {
    setShowEditPanel(false);
    setSelectedFile(null);
  };

  const handleDeleteClick = (item) => {
    setConfirmDelete(item);
  };

  const handleCancelDelete = () => {
    setConfirmDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (confirmDelete) {
      await deleteCIS(confirmDelete.id);
      setConfirmDelete(null);
    }
  };

  const flattenedData = Array.isArray(data)
    ? data.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
    : [];

  const filteredData = flattenedData.filter(item =>
    Object.values(item).some(val =>
      (val?.toString()?.toLowerCase() || '').includes(searchQuery.toLowerCase())
    )
  );

  const cisOrder = [...new Set(flattenedData.map(item => item.cis_name || 'Gravity'))];

  const uniqueCISGroups = Object.entries(
    filteredData.reduce((acc, item) => {
      const group = item.label;
      if (!acc[group]) acc[group] = { items: [], subtotal: null };
      acc[group].items.push(item);
      return acc;
    }, {})
  ).sort(([a], [b]) => cisOrder.indexOf(a) - cisOrder.indexOf(b));

  if (subtotals) {
    uniqueCISGroups.forEach(([label, group]) => {
      group.subtotal = subtotals[label]?.subtotal || null;
    });
  }

  const subtotalByGroup = subtotals
    ? Object.entries(subtotals).reduce((acc, [key, value]) => {
        if (key === 'grandTotal') {
          acc.grandTotal = value;
        } else {
          acc.subtotals[key] = value.subtotal;
        }
        return acc;
      }, { subtotals: {}, grandTotal: null })
    : { subtotals: {}, grandTotal: null };

  const displayedGroups = showAllData
    ? uniqueCISGroups
    : uniqueCISGroups.slice(0, 1).map(([cis_name, group]) => [cis_name, { ...group, items: group.items.slice(0, 8) }]);

  const shouldShowButton = uniqueCISGroups.length > 1 || uniqueCISGroups.some(([, group]) => group.items.length > 8);

  const getErrorMessage = (err, fallback = 'An unknown error occurred') => {
    if (!err) return fallback;
    if (typeof err === 'string') return err;
    return err?.response?.data?.error || err?.response?.data?.message || err?.message || fallback;
  };

  const handleManageData = (value) => {
    if (value === 'import') {
      fileInputRef.current.click();
    } else if (value === 'export') {
      handleExport();
    }
  };

  const handleImportFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setShowImportPanel(true);
      event.target.value = null;
    }
  };

  const handleExport = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('CIS');

    const niaLogoBuffer = await fetch(niaLogo).then(res => res.arrayBuffer());
    const philippinesSealBuffer = await fetch(philippinesSeal).then(res => res.arrayBuffer());

    const niaLogoId = workbook.addImage({
      buffer: new Uint8Array(niaLogoBuffer),
      extension: 'png',
    });

    const philippinesSealId = workbook.addImage({
      buffer: new Uint8Array(philippinesSealBuffer),
      extension: 'png',
    });

    worksheet.addImage(niaLogoId, {
      tl: { col: 0, row: 0 },
      ext: { width: 80, height: 80 },
    });

    worksheet.addImage(philippinesSealId, {
      tl: { col: 2, row: 0 },
      ext: { width: 80, height: 80 },
    });

    worksheet.mergeCells('A3:AI3');
    worksheet.getCell('A3').value = 'Republic of the Philippines';
    worksheet.getCell('A3').font = { name: 'Calibri', size: 18, bold: true };

    worksheet.mergeCells('A4:AI4');
    worksheet.getCell('A4').value = 'NATIONAL IRRIGATION ADMINISTRATION';
    worksheet.getCell('A4').font = { name: 'Calibri', size: 16, bold: true };

    worksheet.mergeCells('A5:AI5');
    worksheet.getCell('A5').value = 'REGIONAL OFFICE NO. VII (CENTRAL VISAYAS)';
    worksheet.getCell('A5').font = { name: 'Calibri', size: 16, bold: true };

    worksheet.mergeCells('A6:AI6');
    worksheet.getCell('A6').value = 'BOHOL-CEBU-SIQUIJOR IRRIGATION MANAGEMENT OFFICE';
    worksheet.getCell('A6').font = { name: 'Calibri', size: 16, bold: true };

    worksheet.mergeCells('A7:AI7');
    worksheet.getCell('A7').value = 'BOHOL-SIQUIJOR OFFICE';
    worksheet.getCell('A7').font = { name: 'Calibri', size: 16, color: { argb: '87CEEB' } };

    worksheet.mergeCells('A8:AI8');
    worksheet.getCell('A8').value = headerImo;
    worksheet.getCell('A8').font = { name: 'Calibri', size: 14 };

    worksheet.mergeCells('A9:AI9');
    worksheet.getCell('A9').value = headerProfileDate;
    worksheet.getCell('A9').font = { name: 'Calibri', size: 14 };

    worksheet.mergeCells('A10:AI10');
    worksheet.getCell('A10').value = headerRegion;
    worksheet.getCell('A10').font = { name: 'Calibri', size: 14 };

    worksheet.mergeCells('A11:AI11');
    worksheet.getCell('A11').value = 'PROVINCE : SIQUIJOR';
    worksheet.getCell('A11').font = { name: 'Calibri', size: 14 };

    worksheet.addRow([]);

    const mainHeaders = [
      'ACTIONS', 'NO', 'NAME OF IA', 'NO', 'NAME OF CIS', 'DISTRICT NO.', 'MUNICIPALITY', 'MAILING ADDRESS', 'NAME OF PRESIDENT',
      'CONTACT NUMBER', 'EMAIL ADDRESS', 'DATE IA ORGANIZED', 'SEC REGISTRATION', 'SEC REGISTRATION', 'TAX IDENTIFICATION NUMBER (TIN)',
      'WATER PERMIT', 'WATER PERMIT', 'MEMBERSHIP', 'MEMBERSHIP', 'sectorNum', 'serviceArea', 'fusa', 'operational',
      'ORIGINAL CONSTRUCTION', 'ORIGINAL CONSTRUCTION', 'ORIGINAL CONSTRUCTION', 'ORIGINAL CONSTRUCTION', 'ia_existContract',
      'IMT CONTRACT', 'IMT CONTRACT', 'canalLength_lined', 'canalLength_unlined', 'total_CanalLength', 'FUNDSOURCE', 'CATEGORY',
      'PHASES FOR DEVOLUTION', 'IDO ASSIGNED', 'REMARKS',
    ];

    const mainHeaderRow = worksheet.addRow(mainHeaders);
    mainHeaderRow.eachCell((cell, colNumber) => {
      cell.font = { size: 12, bold: true, color: { argb: 'FFFFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '2E7D32' } };
      cell.alignment = { horizontal: 'center', wrapText: true };
      cell.border = {
        top: { style: 'thin', color: { argb: '000' } },
        bottom: { style: 'thin', color: { argb: '000' } },
        left: { style: 'thin', color: { argb: '000' } },
        right: { style: 'thin', color: { argb: '000' } },
      };
      if ([13, 15, 17, 23, 29, 31].includes(colNumber)) {
        if (colNumber === 13) worksheet.mergeCells(`M${mainHeaderRow.number}:N${mainHeaderRow.number}`);
        if (colNumber === 15) worksheet.mergeCells(`P${mainHeaderRow.number}:Q${mainHeaderRow.number}`);
        if (colNumber === 17) worksheet.mergeCells(`R${mainHeaderRow.number}:S${mainHeaderRow.number}`);
        if (colNumber === 23) worksheet.mergeCells(`X${mainHeaderRow.number}:AA${mainHeaderRow.number}`);
        if (colNumber === 29) worksheet.mergeCells(`AC${mainHeaderRow.number}:AD${mainHeaderRow.number}`);
        if (colNumber === 31) worksheet.mergeCells(`AF${mainHeaderRow.number}:AG${mainHeaderRow.number}`);
      }
    });

    const subHeaders = [
      '', '', '', '', '', '', '', '', '', '', '', '', 'REGISTRATION NO.', 'DATE APPROVED', '',
      'PERMIT NO.', 'DATE APPROVED', 'membership_fb', 'membership_actual', '', '', '', '',
      'DATE OF MOA SIGNED', 'F-SOURCE', 'projCost', 'DATE OF TURNOVER', '',
      'DATE OF SIGNING', 'imt_undercontract', '', '', '', '', '', '', '', '',
    ];
    const subHeaderRow = worksheet.addRow(subHeaders);
    subHeaderRow.eachCell((cell, colNumber) => {
      cell.font = { size: 12, bold: true, color: { argb: 'FFFFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '4CAF50' } };
      cell.alignment = { horizontal: 'center', wrapText: true };
      cell.border = {
        top: { style: 'thin', color: { argb: '000' } },
        bottom: { style: 'thin', color: { argb: '000' } },
        left: { style: 'thin', color: { argb: '000' } },
        right: { style: 'thin', color: { argb: '000' } },
      };
    });

    let rowIndex = 1;
    displayedGroups.forEach(([label, group]) => {
      const groupRow = worksheet.addRow([label.toUpperCase()]);
      worksheet.mergeCells(`A${groupRow.number}:AI${groupRow.number}`);
      groupRow.eachCell(cell => {
        cell.font = { size: 12, bold: true };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'CADABA' } };
        cell.alignment = { horizontal: 'left', vertical: 'middle' };
        cell.border = {
          top: { style: 'thin', color: { argb: '000' } },
          bottom: { style: 'thin', color: { argb: '000' } },
          left: { style: 'thin', color: { argb: '000' } },
          right: { style: 'thin', color: { argb: '000' } },
        };
      });
      worksheet.addRow([]);

      let currentIA = null;
      group.items.forEach((item) => {
        const row = worksheet.addRow([
          '',
          item.num || '',
          item.ia_name || '',
          item.num || '',
          item.cis_name || '',
          item.districtNum || '',
          item.municipality || '',
          item.mailing_address || '',
          item.president || '',
          item.contactNum || '',
          item.email_address || '',
          item.dateOrganized || '',
          item.sec_RegistrationNum || '',
          item.sec_DateApproved || '',
          item.tin || '',
          item.water_permitNum || '',
          item.water_dateApproved || '',
          item.membership_fb ? Number(item.membership_fb).toFixed(3) : '',
          item.membership_actual ? Number(item.membership_actual).toFixed(3) : '',
          item.sectorNum ? Number(item.sectorNum).toFixed(3) : '',
          item.serviceArea ? Number(item.serviceArea).toFixed(3) : '',
          item.fusa ? Number(item.fusa).toFixed(3) : '',
          item.operational ? Number(item.operational).toFixed(3) : '',
          item.date_moaSigned || '',
          item.f_source || '',
          item.projCost ? Number(item.projCost).toFixed(3) : '',
          item.date_turnOver || '',
          item.ia_existContract ? Number(item.ia_existContract).toFixed(3) : '',
          item.imt_dateSigned || '',
          item.imt_undercontract ? Number(item.imt_undercontract).toFixed(3) : '',
          item.canalLength_lined ? Number(item.canalLength_lined).toFixed(3) : '',
          item.canalLength_unlined ? Number(item.canalLength_unlined).toFixed(3) : '',
          item.total_CanalLength ? Number(item.total_CanalLength).toFixed(3) : '',
          item.fundSource || '',
          item.category || '',
          item.devolutionPhases || '',
          item.idoAssigned || '',
          item.remarks || '',
        ]);
        row.eachCell((cell, colNumber) => {
          cell.font = { size: 12 };
          cell.border = {
            top: { style: 'thin', color: { argb: '000' } },
            bottom: { style: 'thin', color: { argb: '000' } },
            left: { style: 'thin', color: { argb: '000' } },
            right: { style: 'thin', color: { argb: '000' } },
          };
          cell.alignment = [2, 4, 6, 7, 8, 9, 10].includes(colNumber)
            ? { horizontal: 'left', vertical: 'top', wrapText: true }
            : [18, 19, 20, 21, 22, 23, 26, 28, 30, 31, 32].includes(colNumber)
            ? { horizontal: 'right', vertical: 'top', wrapText: true }
            : { horizontal: 'center', vertical: 'top', wrapText: true };
        });

        if (item.ia_name !== currentIA) {
          currentIA = item.ia_name;
          const subtotalKey = `${label}_${item.ia_name}`;
          const subtotal = subtotalByGroup.subtotals[subtotalKey];
          if (subtotal) {
            const subtotalRow = worksheet.addRow([
              '',
              `Subtotal: ${subtotal.ia_name}`, '', '', '', '', '', '', '', '', '',
              '', '', '', '', '',
              subtotal.membership_fb ? Number(subtotal.membership_fb).toFixed(3) : '',
              subtotal.membership_actual ? Number(subtotal.membership_actual).toFixed(3) : '',
              subtotal.sectorNum ? Number(subtotal.sectorNum).toFixed(3) : '',
              subtotal.serviceArea ? Number(subtotal.serviceArea).toFixed(3) : '',
              subtotal.fusa ? Number(subtotal.fusa).toFixed(3) : '',
              subtotal.operational ? Number(subtotal.operational).toFixed(3) : '',
              '',
              '',
              subtotal.projCost ? Number(subtotal.projCost).toFixed(3) : '',
              '',
              subtotal.ia_existContract ? Number(subtotal.ia_existContract).toFixed(3) : '',
              '',
              subtotal.imt_undercontract ? Number(subtotal.imt_undercontract).toFixed(3) : '',
              subtotal.canalLength_lined ? Number(subtotal.canalLength_lined).toFixed(3) : '',
              subtotal.canalLength_unlined ? Number(subtotal.canalLength_unlined).toFixed(3) : '',
              subtotal.total_CanalLength ? Number(subtotal.total_CanalLength).toFixed(3) : '',
              '', '', '', '', '',
            ]);
            worksheet.mergeCells(`B${subtotalRow.number}:P${subtotalRow.number}`);
            subtotalRow.eachCell(cell => {
              cell.font = { size: 12, bold: true };
              cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFF00' } };
              cell.alignment = { horizontal: cell.col >= 18 ? 'right' : 'left', vertical: 'middle' };
              cell.border = {
                top: { style: 'thin', color: { argb: '000' } },
                bottom: { style: 'thin', color: { argb: '000' } },
                left: { style: 'thin', color: { argb: '000' } },
                right: { style: 'thin', color: { argb: '000' } },
              };
            });
            worksheet.addRow([]);
          }
        }
      });

      if (group.subtotal) {
        const cisSubtotalRow = worksheet.addRow([
          '',
          `Subtotal: ${label}`, '', '', '', '', '', '', '', '', '',
          '', '', '', '', '',
          group.subtotal.membership_fb ? Number(group.subtotal.membership_fb).toFixed(3) : '',
          group.subtotal.membership_actual ? Number(group.subtotal.membership_actual).toFixed(3) : '',
          group.subtotal.sectorNum ? Number(group.subtotal.sectorNum).toFixed(3) : '',
          group.subtotal.serviceArea ? Number(group.subtotal.serviceArea).toFixed(3) : '',
          group.subtotal.fusa ? Number(group.subtotal.fusa).toFixed(3) : '',
          group.subtotal.operational ? Number(group.subtotal.operational).toFixed(3) : '',
          '',
          '',
          group.subtotal.projCost ? Number(group.subtotal.projCost).toFixed(3) : '',
          '',
          group.subtotal.ia_existContract ? Number(group.subtotal.ia_existContract).toFixed(3) : '',
          '',
          group.subtotal.imt_undercontract ? Number(group.subtotal.imt_undercontract).toFixed(3) : '',
          group.subtotal.canalLength_lined ? Number(group.subtotal.canalLength_lined).toFixed(3) : '',
          group.subtotal.canalLength_unlined ? Number(group.subtotal.canalLength_unlined).toFixed(3) : '',
          group.subtotal.total_CanalLength ? Number(group.subtotal.total_CanalLength).toFixed(3) : '',
          '', '', '', '', '',
        ]);
        worksheet.mergeCells(`B${cisSubtotalRow.number}:P${cisSubtotalRow.number}`);
        cisSubtotalRow.eachCell(cell => {
          cell.font = { size: 12, bold: true };
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFF00' } };
          cell.alignment = { horizontal: cell.col >= 18 ? 'right' : 'left', vertical: 'middle' };
          cell.border = {
            top: { style: 'thin', color: { argb: '000' } },
            bottom: { style: 'thin', color: { argb: '000' } },
            left: { style: 'thin', color: { argb: '000' } },
            right: { style: 'thin', color: { argb: '000' } },
          };
        });
        worksheet.addRow([]);
      }
    });

    if (subtotalByGroup.grandTotal) {
      const grandTotalRow = worksheet.addRow([
        '',
        `Grand Total: ${subtotalByGroup.grandTotal.ia_name || 'All'}`, '', '', '', '', '', '', '', '', '',
        '', '', '', '', '',
        subtotalByGroup.grandTotal.membership_fb ? Number(subtotalByGroup.grandTotal.membership_fb).toFixed(3) : '',
        subtotalByGroup.grandTotal.membership_actual ? Number(subtotalByGroup.grandTotal.membership_actual).toFixed(3) : '',
        subtotalByGroup.grandTotal.sectorNum ? Number(subtotalByGroup.grandTotal.sectorNum).toFixed(3) : '',
        subtotalByGroup.grandTotal.serviceArea ? Number(subtotalByGroup.grandTotal.serviceArea).toFixed(3) : '',
        subtotalByGroup.grandTotal.fusa ? Number(subtotalByGroup.grandTotal.fusa).toFixed(3) : '',
        subtotalByGroup.grandTotal.operational ? Number(subtotalByGroup.grandTotal.operational).toFixed(3) : '',
        '',
        '',
        subtotalByGroup.grandTotal.projCost ? Number(subtotalByGroup.grandTotal.projCost).toFixed(3) : '',
        '',
        subtotalByGroup.grandTotal.ia_existContract ? Number(subtotalByGroup.grandTotal.ia_existContract).toFixed(3) : '',
        '',
        subtotalByGroup.grandTotal.imt_undercontract ? Number(subtotalByGroup.grandTotal.imt_undercontract).toFixed(3) : '',
        subtotalByGroup.grandTotal.canalLength_lined ? Number(subtotalByGroup.grandTotal.canalLength_lined).toFixed(3) : '',
        subtotalByGroup.grandTotal.canalLength_unlined ? Number(subtotalByGroup.grandTotal.canalLength_unlined).toFixed(3) : '',
        subtotalByGroup.grandTotal.total_CanalLength ? Number(subtotalByGroup.grandTotal.total_CanalLength).toFixed(3) : '',
        '', '', '', '', '',
      ]);
      worksheet.mergeCells(`B${grandTotalRow.number}:P${grandTotalRow.number}`);
      grandTotalRow.eachCell(cell => {
        cell.font = { size: 14, bold: true };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFF00' } };
        cell.alignment = { horizontal: cell.col >= 18 ? 'right' : 'left', vertical: 'middle' };
        cell.border = {
          top: { style: 'thin', color: { argb: '000' } },
          bottom: { style: 'thin', color: { argb: '000' } },
          left: { style: 'thin', color: { argb: '000' } },
          right: { style: 'thin', color: { argb: '000' } },
        };
      });
      worksheet.addRow([]);
    }

    worksheet.columns = [
      { key: 'actions', width: 5 },
      { key: 'num', width: 5 },
      { key: 'ia_name', width: 30 },
      { key: 'num2', width: 5 },
      { key: 'cis_name', width: 25 },
      { key: 'districtNum', width: 10 },
      { key: 'municipality', width: 25 },
      { key: 'mailing_address', width: 25 },
      { key: 'president', width: 25 },
      { key: 'contactNum', width: 15 },
      { key: 'email_address', width: 20 },
      { key: 'dateOrganized', width: 15 },
      { key: 'sec_RegistrationNum', width: 15 },
      { key: 'sec_DateApproved', width: 15 },
      { key: 'tin', width: 15 },
      { key: 'water_permitNum', width: 15 },
      { key: 'water_dateApproved', width: 15 },
      { key: 'membership_fb', width: 10 },
      { key: 'membership_actual', width: 10 },
      { key: 'sectorNum', width: 10 },
      { key: 'serviceArea', width: 15 },
      { key: 'fusa', width: 10 },
      { key: 'operational', width: 15 },
      { key: 'date_moaSigned', width: 15 },
      { key: 'f_source', width: 15 },
      { key: 'projCost', width: 15 },
      { key: 'date_turnOver', width: 15 },
      { key: 'ia_existContract', width: 15 },
      { key: 'imt_dateSigned', width: 15 },
      { key: 'imt_undercontract', width: 15 },
      { key: 'canalLength_lined', width: 10 },
      { key: 'canalLength_unlined', width: 10 },
      { key: 'total_CanalLength', width: 15 },
      { key: 'fundSource', width: 15 },
      { key: 'category', width: 15 },
      { key: 'devolutionPhases', width: 15 },
      { key: 'idoAssigned', width: 15 },
      { key: 'remarks', width: 20 },
    ];

    worksheet.getRow(1).height = 60;
    worksheet.getRow(2).height = 60;
    worksheet.getRow(3).height = 30;
    worksheet.getRow(4).height = 25;
    worksheet.getRow(5).height = 25;
    worksheet.getRow(6).height = 25;
    worksheet.getRow(7).height = 25;
    worksheet.getRow(8).height = 20;
    worksheet.getRow(9).height = 20;
    worksheet.getRow(10).height = 20;
    worksheet.getRow(11).height = 20;

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    if (window.isSecureContext && 'showSaveFilePicker' in window) {
      try {
        const options = {
          suggestedName: 'CIS_IA_Profile_BIIS_2025.xlsx',
          types: [{
            description: 'Excel File',
            accept: {
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx']
            }
          }]
        };
        const handle = await window.showSaveFilePicker(options);
        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();
      } catch (err) {
        if (err.name !== 'AbortError') {
          alert('Save failed due to an unexpected error. Check the console.');
        }
      }
    } else {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'CIS_IA_Profile_BIIS_2025.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    }
  };

  if (yearLoading || loading || subtotalsLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          backgroundColor: darkMode ? 'black' : 'white',
        }}
      >
        <Loader />
      </div>
    );
  }

  if (error || subtotalsError) {
    return (
      <div style={{ padding: '20px', color: darkMode ? '#fff' : '#333' }}>
        <p>Error: {getErrorMessage(error || subtotalsError)}</p>
      </div>
    );
  }

  return (
  <>
    {addSuccessMessage && (
      <div
        style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          backgroundColor: '#4caf50',
          color: '#fff',
          padding: '10px 20px',
          borderRadius: '5px',
          boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
          zIndex: 9999
        }}
      >
        {addSuccessMessage}
      </div>
    )}
    <SlidingPanel darkMode={darkMode} show={true} isExpanded={isSidebarExpanded}>
      <div style={{ display: 'flex', height: '100vh' }}>
        {showImportPanel ? (
          <Container darkMode={darkMode} isSidebarExpanded={isSidebarExpanded} showRightPanel={false}>
            <CISboholImport
              darkMode={darkMode}
              onClose={() => setShowImportPanel(false)}
              onSave={handleImportSuccess}
              selectedYear={selectedYear}
              setSelectedYear={setSelectedYear}
              importedFile={selectedFile}
            />
          </Container>
        ) : (
          <>
            <Container darkMode={darkMode} isSidebarExpanded={isSidebarExpanded} showRightPanel={showRightPanel || showEditPanel}>
              <HeaderContainer>
                <HeaderLeft>
                  <HeaderText>
                    <ProfileText>
                      {isEditingHeader && showControls ? (
                        <>
                          <input value={headerImo} onChange={(e) => setHeaderImo(e.target.value)} />
                          <input value={headerProfileDate} onChange={(e) => setHeaderProfileDate(e.target.value)} />
                          <input value={headerRegion} onChange={(e) => setHeaderRegion(e.target.value)} />
                          <button onClick={saveHeader}>Save</button>
                          <button onClick={() => setIsEditingHeader(false)}>Cancel</button>
                        </>
                      ) : (
                        <>
                          <h2>{headerImo}</h2>
                          <p>{headerProfileDate}</p>
                          <p>{headerRegion}</p>
                          <p>PROVINCE : SIQUIJOR</p>
                          {showControls && (
                            <button onClick={() => setIsEditingHeader(true)}>Edit</button>
                          )}
                        </>
                      )}
                    </ProfileText>
                  </HeaderText>
                  <YearGroup>
                    <label htmlFor="yearSelect">Year:</label>
                    <YearSelect
                      id="yearSelect"
                      value={selectedYear || ''}
                      onChange={(e) => setSelectedYear(Number(e.target.value))}
                    >
                      <option value="" disabled>Select Year</option>
                      {(!Array.isArray(yearData) || yearData.length === 0) && (
                        <option value={new Date().getFullYear()}>{new Date().getFullYear()} (Current)</option>
                      )}
                      {Array.isArray(yearData) &&
                        yearData.map((year, index) => (
                          <option key={index} value={year}>
                            {year}
                          </option>
                        ))}
                    </YearSelect>
                    {yearError && (
                      <div style={{ marginTop: '6px' }}>
                        <small style={{ color: darkMode ? '#ffb4b4' : '#b00020', display: 'block', marginBottom: '6px' }}>
                          Year source error: {getErrorMessage(yearError)}
                        </small>
                        <StyledButton onClick={() => refetchYears()}>
                          Retry Fetching Years
                        </StyledButton>
                      </div>
                    )}
                  </YearGroup>
                </HeaderLeft>
                {!hideControlsForViewOnly && (
                <RightControls>
                  <SearchInput
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  <StyledButton onClick={() => setShowRightPanel(true)}>
                    Add CIS Siquijor
                  </StyledButton>
                  <ManageDataSelect onChange={(e) => handleManageData(e.target.value)}>
                    <option value="">Manage Data</option>
                    <option value="import">Import</option>
                    <option value="export">Export</option>
                  </ManageDataSelect>
                  <input
                    type="file"
                    accept=".xlsx"
                    onChange={handleImportFileSelect}
                    style={{ display: 'none' }}
                    ref={fileInputRef}
                  />
                </RightControls>
                )}
              </HeaderContainer>
              <TableContainer>
                <TableWrapper isExpanded={isSidebarExpanded} id="tableWrapper">
                  <div id="scrollContent" style={{ width: '100%', overflowX: 'auto' }}>
                    <Table darkMode={darkMode}>
                      <thead>
                        <tr>
                          <TableHeader darkMode={darkMode}>ACTIONS</TableHeader>
                          <TableHeader darkMode={darkMode}>NO</TableHeader>
                          <TableHeader darkMode={darkMode}>NAME OF IA</TableHeader>
                          <TableHeader darkMode={darkMode}>NO</TableHeader>
                          <TableHeader darkMode={darkMode}>NAME OF CIS</TableHeader>
                          <TableHeader darkMode={darkMode}>DISTRICT NO.</TableHeader>
                          <TableHeader darkMode={darkMode}>MUNICIPALITY</TableHeader>
                          <TableHeader darkMode={darkMode}>MAILING ADDRESS</TableHeader>
                          <TableHeader darkMode={darkMode}>NAME OF PRESIDENT</TableHeader>
                          <TableHeader darkMode={darkMode}>CONTACT NUMBER</TableHeader>
                          <TableHeader darkMode={darkMode}>EMAIL ADDRESS</TableHeader>
                          <TableHeader darkMode={darkMode}>DATE IA ORGANIZED</TableHeader>
                          <TableHeader darkMode={darkMode} colSpan="2" style={{ textAlign: 'center' }}>
                            SEC REGISTRATION
                          </TableHeader>
                          <TableHeader darkMode={darkMode}>TAX IDENTIFICATION NUMBER (TIN)</TableHeader>
                          <TableHeader darkMode={darkMode} colSpan="2" style={{ textAlign: 'center' }}>
                            WATER PERMIT
                          </TableHeader>
                          <TableHeader darkMode={darkMode} colSpan="2" style={{ textAlign: 'center' }}>
                            MEMBERSHIP
                          </TableHeader>
                          <TableHeader darkMode={darkMode}>sectorNum</TableHeader>
                          <TableHeader darkMode={darkMode}>serviceArea</TableHeader>
                          <TableHeader darkMode={darkMode}>fusa</TableHeader>
                          <TableHeader darkMode={darkMode}>operational</TableHeader>
                          <TableHeader darkMode={darkMode} colSpan="4" style={{ textAlign: 'center' }}>
                            ORIGINAL CONSTRUCTION
                          </TableHeader>
                          <TableHeader darkMode={darkMode}>ia_existContract</TableHeader>
                          <TableHeader darkMode={darkMode} colSpan="2" style={{ textAlign: 'center' }}>
                            IMT CONTRACT
                          </TableHeader>
                          <TableHeader darkMode={darkMode} colSpan="2" style={{ textAlign: 'center' }}>
                            CANAL LENGTH
                          </TableHeader>
                          <TableHeader darkMode={darkMode}>total_CanalLength</TableHeader>
                          <TableHeader darkMode={darkMode}>FUNDSOURCE</TableHeader>
                          <TableHeader darkMode={darkMode}>CATEGORY</TableHeader>
                          <TableHeader darkMode={darkMode}>PHASES FOR DEVOLUTION</TableHeader>
                          <TableHeader darkMode={darkMode}>IDO ASSIGNED</TableHeader>
                          <TableHeader darkMode={darkMode}>REMARKS</TableHeader>
                        </tr>
                        <tr>
                          <TableHeaderS darkMode={darkMode}></TableHeaderS>
                          <TableHeaderS darkMode={darkMode}></TableHeaderS>
                          <TableHeaderS darkMode={darkMode}></TableHeaderS>
                          <TableHeaderS darkMode={darkMode}></TableHeaderS>
                          <TableHeaderS darkMode={darkMode}></TableHeaderS>
                          <TableHeaderS darkMode={darkMode}></TableHeaderS>
                          <TableHeaderS darkMode={darkMode}></TableHeaderS>
                          <TableHeaderS darkMode={darkMode}></TableHeaderS>
                          <TableHeaderS darkMode={darkMode}></TableHeaderS>
                          <TableHeaderS darkMode={darkMode}></TableHeaderS>
                          <TableHeaderS darkMode={darkMode}></TableHeaderS>
                          <TableHeaderS darkMode={darkMode}></TableHeaderS>
                          <TableHeaderS darkMode={darkMode}>REGISTRATION NO.</TableHeaderS>
                          <TableHeaderS darkMode={darkMode}>DATE APPROVED</TableHeaderS>
                          <TableHeaderS darkMode={darkMode}></TableHeaderS>
                          <TableHeaderS darkMode={darkMode}>PERMIT NO.</TableHeaderS>
                          <TableHeaderS darkMode={darkMode}>DATE APPROVED</TableHeaderS>
                          <TableHeaderS darkMode={darkMode}>membership_fb</TableHeaderS>
                          <TableHeaderS darkMode={darkMode}>membership_actual</TableHeaderS>
                          <TableHeaderS darkMode={darkMode}></TableHeaderS>
                          <TableHeaderS darkMode={darkMode}></TableHeaderS>
                          <TableHeaderS darkMode={darkMode}></TableHeaderS>
                          <TableHeaderS darkMode={darkMode}></TableHeaderS>
                          <TableHeaderS darkMode={darkMode}>DATE OF MOA SIGNED</TableHeaderS>
                          <TableHeaderS darkMode={darkMode}>F-SOURCE</TableHeaderS>
                          <TableHeaderS darkMode={darkMode}>projCost</TableHeaderS>
                          <TableHeaderS darkMode={darkMode}>DATE OF TURNOVER</TableHeaderS>
                          <TableHeaderS darkMode={darkMode}></TableHeaderS>
                          <TableHeaderS darkMode={darkMode}>DATE OF SIGNING</TableHeaderS>
                          <TableHeaderS darkMode={darkMode}>imt_undercontract</TableHeaderS>
                          <TableHeaderS darkMode={darkMode}>canalLength_lined</TableHeaderS>
                          <TableHeaderS darkMode={darkMode}>canalLength_unlined</TableHeaderS>
                          <TableHeaderS darkMode={darkMode}></TableHeaderS>
                          <TableHeaderS darkMode={darkMode}></TableHeaderS>
                          <TableHeaderS darkMode={darkMode}></TableHeaderS>
                          <TableHeaderS darkMode={darkMode}></TableHeaderS>
                          <TableHeaderS darkMode={darkMode}></TableHeaderS>
                          <TableHeaderS darkMode={darkMode}></TableHeaderS>
                        </tr>
                      </thead>
                      <tbody>
                        {displayedGroups.map(([cis_name, group]) => {
                          let previousIA = null;
                          return (
                            <React.Fragment key={cis_name}>
                              <tr>
                                <td
                                  colSpan="100%"
                                  style={{
                                    backgroundColor: '#cadaba',
                                    fontWeight: 'bold',
                                    textAlign: 'left',
                                    paddingLeft: '10px',
                                  }}
                                >
                                  {cis_name.toUpperCase()}
                                </td>
                              </tr>
                              <tr style={{ height: '10px' }}><td colSpan="100%" /></tr>
                              {group.items.reduce((acc, item, index) => {
                                const showIA = item.ia_name !== previousIA;
                                previousIA = item.ia_name;
                                acc.push(
                                  <TableRow
                                    key={`${item.id}-${index}`}
                                    darkMode={darkMode}
                                    onClick={() => handleRowClick(item)}
                                  >
                                    <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '120px', padding: '8px 0' }}>
                                      {showControls ? (
                                        <ButtonGroup>
                                          <StyledButton
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleEditData(item);
                                            }}
                                          >
                                            ✎
                                          </StyledButton>
                                          <StyledButton
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleDeleteClick(item);
                                            }}
                                          >
                                            ❌
                                          </StyledButton>
                                        </ButtonGroup>
                                      ) : (
                                        <ButtonGroup>
                                          <StyledButton style={{ visibility: 'hidden' }}>✎</StyledButton>
                                          <StyledButton style={{ visibility: 'hidden' }}>❌</StyledButton>
                                        </ButtonGroup>
                                      )}
                                    </TableData>
                                    <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '80px' }}>
                                      {item.num || ''}
                                    </TableData>
                                    <TableData darkMode={darkMode} style={{ textAlign: 'left', minWidth: '240px' }}>
                                      {showIA ? item.ia_name : ''}
                                    </TableData>
                                    <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '80px' }}>
                                      {item.num || ''}
                                    </TableData>
                                    <TableData darkMode={darkMode} style={{ textAlign: 'left', minWidth: '200px' }}>
                                      {item.cis_name || ''}
                                    </TableData>
                                    <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '80px' }}>
                                      {item.districtNum || ''}
                                    </TableData>
                                    <TableData darkMode={darkMode} style={{ textAlign: 'left', minWidth: '200px', maxWidth: '200px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                      {item.municipality || ''}
                                    </TableData>
                                    <TableData darkMode={darkMode} style={{ textAlign: 'left', minWidth: '200px', maxWidth: '200px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                      {item.mailing_address || ''}
                                    </TableData>
                                    <TableData darkMode={darkMode} style={{ textAlign: 'left', minWidth: '200px', maxWidth: '200px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                      {item.president || ''}
                                    </TableData>
                                    <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '150px' }}>
                                      {item.contactNum || ''}
                                    </TableData>
                                    <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '200px', maxWidth: '200px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                      {item.email_address || ''}
                                    </TableData>
                                    <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '120px' }}>
                                      {item.dateOrganized || ''}
                                    </TableData>
                                    <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '120px', maxWidth: '120px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                      {item.sec_RegistrationNum || ''}
                                    </TableData>
                                    <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '120px', maxWidth: '120px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                      {item.sec_DateApproved || ''}
                                    </TableData>
                                    <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '120px', maxWidth: '120px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                      {item.tin || ''}
                                    </TableData>
                                    <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '120px' }}>
                                      {item.water_permitNum || ''}
                                    </TableData>
                                    <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '120px' }}>
                                      {item.water_dateApproved || ''}
                                    </TableData>
                                    <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '80px' }}>
                                      {item.membership_fb ? Number(item.membership_fb).toFixed(3) : ''}
                                    </TableData>
                                    <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '80px' }}>
                                      {item.membership_actual ? Number(item.membership_actual).toFixed(3) : ''}
                                    </TableData>
                                    <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '80px' }}>
                                      {item.sectorNum ? Number(item.sectorNum).toFixed(3) : ''}
                                    </TableData>
                                    <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px' }}>
                                      {item.serviceArea ? Number(item.serviceArea).toFixed(3) : ''}
                                    </TableData>
                                    <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '80px' }}>
                                      {item.fusa ? Number(item.fusa).toFixed(3) : ''}
                                    </TableData>
                                    <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px' }}>
                                      {item.operational ? Number(item.operational).toFixed(3) : ''}
                                    </TableData>
                                    <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '120px' }}>
                                      {item.date_moaSigned || ''}
                                    </TableData>
                                    <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '120px' }}>
                                      {item.f_source || ''}
                                    </TableData>
                                    <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px' }}>
                                      {item.projCost ? Number(item.projCost).toFixed(3) : ''}
                                    </TableData>
                                    <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '120px' }}>
                                      {item.date_turnOver || ''}
                                    </TableData>
                                    <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px' }}>
                                      {item.ia_existContract ? Number(item.ia_existContract).toFixed(3) : ''}
                                    </TableData>
                                    <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '120px' }}>
                                      {item.imt_dateSigned || ''}
                                    </TableData>
                                    <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px' }}>
                                      {item.imt_undercontract ? Number(item.imt_undercontract).toFixed(3) : ''}
                                    </TableData>
                                    <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '80px' }}>
                                      {item.canalLength_lined ? Number(item.canalLength_lined).toFixed(3) : ''}
                                    </TableData>
                                    <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '80px' }}>
                                      {item.canalLength_unlined ? Number(item.canalLength_unlined).toFixed(3) : ''}
                                    </TableData>
                                    <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px' }}>
                                      {item.total_CanalLength ? Number(item.total_CanalLength).toFixed(3) : ''}
                                    </TableData>
                                    <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '120px' }}>
                                      {item.fundSource || ''}
                                    </TableData>
                                    <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '120px' }}>
                                      {item.category || ''}
                                    </TableData>
                                    <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '120px' }}>
                                      {item.devolutionPhases || ''}
                                    </TableData>
                                    <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '120px' }}>
                                      {item.idoAssigned || ''}
                                    </TableData>
                                    <TableData darkMode={darkMode} style={{ textAlign: 'center', minWidth: '120px' }}>
                                      {item.remarks || ''}
                                    </TableData>
                                  </TableRow>
                                );

                                if (showIA) {
                                  const subtotalKey = `${cis_name}_${item.ia_name}`;
                                  const subtotal = subtotalByGroup.subtotals[subtotalKey];
                                  if (subtotal) {
                                    acc.push(
                                      <tr key={`subtotal-ia-${subtotalKey}`}>
                                        <td colSpan="17" style={{ backgroundColor: '#ffff00', fontWeight: 'bold', textAlign: 'left', paddingLeft: '10px' }}>
                                          Subtotal: {subtotal.ia_name}
                                        </td>
                                        <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '80px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '12px' }}>
                                          {subtotal.membership_fb ? Number(subtotal.membership_fb).toFixed(3) : ''}
                                        </TableData>
                                        <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '80px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '12px' }}>
                                          {subtotal.membership_actual ? Number(subtotal.membership_actual).toFixed(3) : ''}
                                        </TableData>
                                        <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '80px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '12px' }}>
                                          {subtotal.sectorNum ? Number(subtotal.sectorNum).toFixed(3) : ''}
                                        </TableData>
                                        <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '12px' }}>
                                          {subtotal.serviceArea ? Number(subtotal.serviceArea).toFixed(3) : ''}
                                        </TableData>
                                        <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '80px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '12px' }}>
                                          {subtotal.fusa ? Number(subtotal.fusa).toFixed(3) : ''}
                                        </TableData>
                                        <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '12px' }}>
                                          {subtotal.operational ? Number(subtotal.operational).toFixed(3) : ''}
                                        </TableData>
                                        <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '12px' }} />
                                        <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '12px' }} />
                                        <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '12px' }}>
                                          {subtotal.projCost ? Number(subtotal.projCost).toFixed(3) : ''}
                                        </TableData>
                                        <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '12px' }} />
                                        <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '12px' }}>
                                          {subtotal.ia_existContract ? Number(subtotal.ia_existContract).toFixed(3) : ''}
                                        </TableData>
                                        <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '12px' }} />
                                        <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '12px' }}>
                                          {subtotal.imt_undercontract ? Number(subtotal.imt_undercontract).toFixed(3) : ''}
                                        </TableData>
                                        <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '80px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '12px' }}>
                                          {subtotal.canalLength_lined ? Number(subtotal.canalLength_lined).toFixed(3) : ''}
                                        </TableData>
                                        <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '80px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '12px' }}>
                                          {subtotal.canalLength_unlined ? Number(subtotal.canalLength_unlined).toFixed(3) : ''}
                                        </TableData>
                                        <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '12px' }}>
                                          {subtotal.total_CanalLength ? Number(subtotal.total_CanalLength).toFixed(3) : ''}
                                        </TableData>
                                        <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '12px' }} />
                                        <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '12px' }} />
                                        <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '12px' }} />
                                        <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '12px' }} />
                                        <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '12px' }} />
                                      </tr>,
                                      <tr key={`spacer-ia-${subtotalKey}`} style={{ height: '10px' }}><td colSpan="100%" /></tr>
                                    );
                                  }
                                }
                                return acc;
                              }, [])}
                              {group.subtotal && (
                                <>
                                  <tr key={`subtotal-cis-${cis_name}`}>
                                    <td colSpan="17" style={{ backgroundColor: '#ffff00', fontWeight: 'bold', textAlign: 'left', paddingLeft: '10px' }}>
                                      Subtotal: {cis_name}
                                    </td>
                                    <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '80px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '12px' }}>
                                      {group.subtotal.membership_fb ? Number(group.subtotal.membership_fb).toFixed(3) : ''}
                                    </TableData>
                                    <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '80px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '12px' }}>
                                      {group.subtotal.membership_actual ? Number(group.subtotal.membership_actual).toFixed(3) : ''}
                                    </TableData>
                                    <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '80px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '12px' }}>
                                      {group.subtotal.sectorNum ? Number(group.subtotal.sectorNum).toFixed(3) : ''}
                                    </TableData>
                                    <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '12px' }}>
                                      {group.subtotal.serviceArea ? Number(group.subtotal.serviceArea).toFixed(3) : ''}
                                    </TableData>
                                    <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '80px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '12px' }}>
                                      {group.subtotal.fusa ? Number(group.subtotal.fusa).toFixed(3) : ''}
                                    </TableData>
                                    <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '12px' }}>
                                      {group.subtotal.operational ? Number(group.subtotal.operational).toFixed(3) : ''}
                                    </TableData>
                                    <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '12px' }} />
                                    <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '12px' }} />
                                    <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '12px' }}>
                                      {group.subtotal.projCost ? Number(group.subtotal.projCost).toFixed(3) : ''}
                                    </TableData>
                                    <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '12px' }} />
                                    <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '12px' }}>
                                      {group.subtotal.ia_existContract ? Number(group.subtotal.ia_existContract).toFixed(3) : ''}
                                    </TableData>
                                    <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '12px' }} />
                                    <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '12px' }}>
                                      {group.subtotal.imt_undercontract ? Number(group.subtotal.imt_undercontract).toFixed(3) : ''}
                                    </TableData>
                                    <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '80px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '12px' }}>
                                      {group.subtotal.canalLength_lined ? Number(group.subtotal.canalLength_lined).toFixed(3) : ''}
                                    </TableData>
                                    <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '80px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '12px' }}>
                                      {group.subtotal.canalLength_unlined ? Number(group.subtotal.canalLength_unlined).toFixed(3) : ''}
                                    </TableData>
                                    <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '12px' }}>
                                      {group.subtotal.total_CanalLength ? Number(group.subtotal.total_CanalLength).toFixed(3) : ''}
                                    </TableData>
                                    <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '12px' }} />
                                    <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '12px' }} />
                                    <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '12px' }} />
                                    <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '12px' }} />
                                    <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '12px' }} />
                                  </tr>
                                  <tr key={`spacer-cis-${cis_name}`} style={{ height: '10px' }}><td colSpan="100%" /></tr>
                                </>
                              )}
                            </React.Fragment>
                          );
                        })}
                        {subtotalByGroup.grandTotal && (
                          <>
                            <tr key="grand-total">
                              <td colSpan="17" style={{ backgroundColor: '#ffff00', fontWeight: 'bold', textAlign: 'left', paddingLeft: '10px', fontSize: '14px' }}>
                                Grand Total: {subtotalByGroup.grandTotal.ia_name || 'All'}
                              </td>
                              <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '80px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '14px' }}>
                                {subtotalByGroup.grandTotal.membership_fb ? Number(subtotalByGroup.grandTotal.membership_fb).toFixed(3) : ''}
                              </TableData>
                              <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '80px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '14px' }}>
                                {subtotalByGroup.grandTotal.membership_actual ? Number(subtotalByGroup.grandTotal.membership_actual).toFixed(3) : ''}
                              </TableData>
                              <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '80px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '14px' }}>
                                {subtotalByGroup.grandTotal.sectorNum ? Number(subtotalByGroup.grandTotal.sectorNum).toFixed(3) : ''}
                              </TableData>
                              <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '14px' }}>
                                {subtotalByGroup.grandTotal.serviceArea ? Number(subtotalByGroup.grandTotal.serviceArea).toFixed(3) : ''}
                              </TableData>
                              <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '80px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '14px' }}>
                                {subtotalByGroup.grandTotal.fusa ? Number(subtotalByGroup.grandTotal.fusa).toFixed(3) : ''}
                              </TableData>
                              <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '14px' }}>
                                {subtotalByGroup.grandTotal.operational ? Number(subtotalByGroup.grandTotal.operational).toFixed(3) : ''}
                              </TableData>
                              <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '14px' }} />
                              <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '14px' }} />
                              <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '14px' }}>
                                {subtotalByGroup.grandTotal.projCost ? Number(subtotalByGroup.grandTotal.projCost).toFixed(3) : ''}
                              </TableData>
                              <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '14px' }} />
                              <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '14px' }}>
                                {subtotalByGroup.grandTotal.ia_existContract ? Number(subtotalByGroup.grandTotal.ia_existContract).toFixed(3) : ''}
                              </TableData>
                              <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '14px' }} />
                              <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '14px' }}>
                                {subtotalByGroup.grandTotal.imt_undercontract ? Number(subtotalByGroup.grandTotal.imt_undercontract).toFixed(3) : ''}
                              </TableData>
                              <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '80px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '14px' }}>
                                {subtotalByGroup.grandTotal.canalLength_lined ? Number(subtotalByGroup.grandTotal.canalLength_lined).toFixed(3) : ''}
                              </TableData>
                              <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '80px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '14px' }}>
                                {subtotalByGroup.grandTotal.canalLength_unlined ? Number(subtotalByGroup.grandTotal.canalLength_unlined).toFixed(3) : ''}
                              </TableData>
                              <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '14px' }}>
                                {subtotalByGroup.grandTotal.total_CanalLength ? Number(subtotalByGroup.grandTotal.total_CanalLength).toFixed(3) : ''}
                              </TableData>
                              <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '14px' }} />
                              <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '14px' }} />
                              <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '14px' }} />
                              <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '14px' }} />
                              <TableData darkMode={darkMode} style={{ textAlign: 'right', minWidth: '120px', backgroundColor: '#ffff00', fontWeight: 'bold', fontSize: '14px' }} />
                            </tr>
                            <tr style={{ height: '10px' }}><td colSpan="100%" /></tr>
                            </>
                          )}
                          </tbody>
                        </Table>
                        </div>
                        {shouldShowButton && (
                        <StyledButton
                          style={{ margin: '20px auto', display: 'block' }}
                          onClick={() => setShowAllData(!showAllData)}
                        >
                          {showAllData ? 'Show Less' : 'Show All'}
                        </StyledButton>
                        )}
                      </TableWrapper>
                      <div id="bottomScroll" style={{ overflowX: 'auto', width: '100%', height: '16px' }}>
                        <div style={{ width: document.getElementById('tableWrapper')?.querySelector('#scrollContent')?.scrollWidth || '100%' }}></div>
                      </div>
                      </TableContainer>
                    </Container>
                    <RightPanel show={showRightPanel} darkMode={darkMode}>
                      <CISboholAdd
                      darkMode={darkMode}
                      onClose={handleClose}
                      onSuccess={async () => {
                  handleAddSuccess();
                  await refetch();
                }}
                selectedYear={selectedYear}
                setSelectedYear={setSelectedYear}
              />
            </RightPanel>
            <RightPanel show={showEditPanel} darkMode={darkMode}>
              {showEditPanel && (
                <CISboholEdit
                  darkMode={darkMode}
                  onClose={closeEditPanel}
                  selectedItem={selectedFile}
                  onSuccess={async () => {
                    setAddSuccessMessage('Data updated successfully!');
                    setTimeout(() => setAddSuccessMessage(null), 3000);
                    closeEditPanel();
                    await refetch();
                  }}
                />
              )}
            </RightPanel>
          </>
        )}
      </div>
    </SlidingPanel>
    {modalOpen && selectedFile && (
      <CisModal
        isOpen={modalOpen}
        selectedItem={selectedFile}
        onClose={() => setModalOpen(false)}
        onNavigate={onSelect}
        selectedYear={selectedYear}
        sectionKey="CISsiquijor"
      />
    )}
  {confirmDelete && (
                <DeleteConfirmationModal
                  itemName={confirmDelete.ia_name}
                  onCancel={() => setConfirmDelete(null)}
                  onConfirm={async () => {
                    await deleteCIS(confirmDelete.id);
                    refetch();
                    setConfirmDelete(null);
                  }}
                />
              )}
  </>
 );
};

export default CISsiqiojor;

// Styled Components
const Container = styled.div`
  background-color: ${({ darkMode }) => (darkMode ? 'transparent' : '#f2f2f2')};
    background-image: ${({ darkMode }) => (darkMode ? `url(${adminBg})` : 'none')};
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center;
    color: ${({ darkMode }) => (darkMode ? '#757272' : '#333')};
    padding: 80px 20px 20px;
    width: ${({ isSidebarExpanded, showRightPanel }) => (showRightPanel ? '57%' : isSidebarExpanded ? 'calc(100% - 100px)' : '98%')};
    min-height: 100vh;
    overflow-y: auto;
    margin-left: 0;
    box-shadow: -4px 0 10px rgba(0, 0, 0, 0.2);
    transition: width 0.3s ease;
    box-sizing: border-box;
  `;

const TableContainer = styled.div`
  margin-top: 20px;
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  padding-bottom: 60px;
`;

const RightPanel = styled.div`
    position: fixed;
    top: 0;
    right: ${({ show }) => (show ? '0' : '-45%')};
    width: 43%;
    height: 100%;
    background-color: ${({ darkMode }) => (darkMode ? 'transparent' : '#fff')};
    background-image: ${({ darkMode }) => (darkMode ? `url(${adminBg})` : 'none')};
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center;
    box-shadow: -4px 0 10px rgba(0, 0, 0, 0.2);
    z-index: 5;
    padding: 5px;
    box-sizing: border-box;
    transition: right 0.3s ease-in-out;
  `;

const TableRow = styled.tr`
  background: ${({ darkMode }) => (darkMode ? 'rgba(3, 82, 33, 0.3)' : '#fff')};
  backdrop-filter: ${({ darkMode }) => (darkMode ? 'blur(10px)' : 'none')};
  -webkit-backdrop-filter: ${({ darkMode }) => (darkMode ? 'blur(10px)' : 'none')};

  &:hover {
    background-color: ${({ darkMode }) => (darkMode ? 'rgba(255, 255, 255, 0.1)' : '#f5f5f5')};
    cursor: pointer;
  }
`;

const SlidingPanel = styled.div`
  position: fixed;
  top: 0;
  left: ${({ isExpanded }) => (isExpanded ? '180px' : '60px')};
  width: ${({ isExpanded }) => (isExpanded ? 'calc(100% - 180px)' : 'calc(100% - 60px)')};
  height: 100%;
  background-color: ${({ darkMode }) => (darkMode ? 'transparent' : '#ccc')};
  background-image: ${({ darkMode }) => (darkMode ? `url(${adminBg})` : 'none')};
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  box-shadow: -4px 0 10px rgba(0, 0, 0, 0.2);
  transform: translateX(100%);
  transition: transform 0.5s ease, left 0.3s ease-in-out, width 0.3s ease-in-out;
  z-index: 2;
  padding: 20px;
  box-sizing: border-box;

  ${({ show }) => show && `transform: translateX(0);`}
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 0 40px;
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const HeaderText = styled.div`
  margin-bottom: 10px;
  margin-top: -38px;

  h2 {
    margin: 0;
    font-size: 22px;
    color: ${({ darkMode }) => (darkMode ? '#ffffff' : '#333')};
  }

  p {
    margin: 0;
    font-size: 18px;
    color: ${({ darkMode }) => (darkMode ? '#cccccc' : '#555')};
  }
`;

const RightControls = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-end;
  padding-right: 40px;
  margin-top: 60px;
`;



const ButtonGroup = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;
`;

const ShowAllButton = styled.button`
  margin: 20px auto;
  display: block;
  padding: 8px 16px;
  background-color: #eee;
  border: 1px solid #aaa;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
  z-index: 5;

  &:hover {
    background-color: #ddd;
  }
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #4caf50;
  border-radius: 4px;
  background-color: #ffffff;
  color: #333;
  font-size: 14px;
  width: 100%;
  transition: border-color 0.2s ease, background-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #388e3c;
  }

  &:hover {
    background-color: #f1f1f1;
  }

  &::placeholder {
    color: #888;
    font-style: italic;
    font-size: 14px;
  }
`;

const YearSelect = styled.select`
  padding: 6px 10px;
  border: 1px solid #4caf50;
  border-radius: 4px;
  background-color: #ffffff;
  color: #333;
  cursor: pointer;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #388e3c;
  }

  &:hover {
    background-color: #f1f1f1;
  }

  option {
    color: #333;
    background-color: #fff;
  }
`;

const YearGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StyledButton = styled.button`
  margin: 0 2px;
  padding: 6px 10px;
  background-color: #eee;
  border: 1px solid #aaa;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
  font-size: 14px;

  &:hover {
    background-color: #ddd;
  }
`;

const ManageDataSelect = styled.select`
  padding: 6px 10px;
  background-color: #eee;
  border: 1px solid #aaa;
  border-radius: 4px;
  color: #333;
  cursor: pointer;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #388e3c;
  }

  &:hover {
    background-color: #ddd;
  }

  option {
    color: #333;
    background-color: #fff;
  }
`;


const ProfileText = styled.div`
  margin-bottom: 10px;

  p {
    margin: 0;
    font-size: 14px;
    color: ${({ darkMode }) => (darkMode ? '#cccccc' : '#555')};
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 2500px;
  background-image: ${({ darkMode }) => (darkMode ? `url(${adminBg})` : 'none')};
  color: ${({ darkMode }) => (darkMode ? '#fff' : '#333')};
`;

const TableHeader = styled.th`
  position: sticky;
  top: 0;
  z-index: 2;
  background-color: ${({ darkMode }) => (darkMode ? '#f2f2f2' : '#2e7d32')};
  color: ${({ darkMode }) => (darkMode ? '#333' : '#fff')};
  padding: 10px;
  font-size: 14px;
  border: 1px solid #ddd;
  text-align: center;
  vertical-align: middle;

  &:first-child {
    position: static;
    left: auto;
    z-index: auto;
    background-color: ${({ darkMode }) => (darkMode ? '#f2f2f2' : '#2e7d32')};
    min-width: 120px;
  }

  &[colspan="2"], &[colspan="4"] {
    text-align: center;
    font-weight: bold;
  }
`;

const TableHeaderS = styled.th`
  position: sticky;
  top: 39px;
  z-index: 1;
  background-color: ${({ darkMode }) => (darkMode ? '#1b5e20' : '#4caf50')};
  color: #ffffff;
  padding: 0 10px;
  border: 1px solid #ddd;
  font-size: 12px;
  text-align: center;

  &:first-child {
    position: static;
    left: auto;
    z-index: auto;
    background-color: ${({ darkMode }) => (darkMode ? '#1b5e20' : '#4caf50')};
  }
`;

const TableData = styled.td`
  padding: 5px;
  font-size: 12px;
  border: 1px solid #ddd;
  color: ${({ darkMode }) => (darkMode ? '#f2f2f2' : '#333')};
  transition: background-color 0.3s ease, color 0.3s ease;

  &:first-child {
    position: static;
    left: auto;
    z-index: auto;
    background-color: ${({ darkMode }) => (darkMode ? '#333' : '#fff')};
    min-width: 120px;
    text-align: center;
    padding: 8px 0;
  }
`;

const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  overflow-y: visible;
  margin-top: 10px;
  position: relative;
  background-color: ${({ darkMode }) => (darkMode ? 'rgb(42, 48, 42)' : '#f2f2f2')};

  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }

  #scrollContent {
    width: 100%;
    overflow-x: auto;
    scrollbar-width: none;
    -ms-overflow-style: none;
  }

  #scrollContent::-webkit-scrollbar {
    display: none;
  }
`;